import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";
import Layout from "../../layout/Layout.jsx";
import FilterPanel from "./components/FilterPanel.jsx";
import PreviewTable from "./components/PreviewTable.jsx";
import { getBulkExportData, getLocalStorageValues } from "../../services/apiCalls.js";
import { generateBulkReport } from "../../services/pdfGenerator/generateBulkReport.js";
import { generateBulkCSV } from "../../services/pdfGenerator/generateCSV.js";

const DEFAULT_FILTERS = {
  sex: undefined,
  age_min: 18,
  age_max: 100,
  risk_level: undefined,
  has_recommendations: undefined,
};

export default function Export() {
  const { t } = useTranslation("export");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [exportData, setExportData] = useState({ users: [], total_count: 0, filtered_count: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [includeRecommendations, setIncludeRecommendations] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build clean filters object (remove defaults)
      const cleanFilters = {};
      if (filters.sex) cleanFilters.sex = filters.sex;
      if (filters.age_min > 18) cleanFilters.age_min = filters.age_min;
      if (filters.age_max < 100) cleanFilters.age_max = filters.age_max;
      if (filters.risk_level !== undefined) cleanFilters.risk_level = filters.risk_level;
      if (filters.has_recommendations !== undefined) cleanFilters.has_recommendations = filters.has_recommendations;

      const data = await getBulkExportData(cleanFilters);
      setExportData(data);
      setSelectedIds([]);
    } catch (err) {
      setError(t("errors.fetchFailed"));
      console.error("Error fetching export data:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const handleFormatChange = (event, newFormat) => {
    if (newFormat !== null) {
      setExportFormat(newFormat);
    }
  };

  const handleExport = async (exportAll = true) => {
    setExporting(true);
    try {
      const { clinician_name } = getLocalStorageValues();

      // Get users to export
      let usersToExport;
      if (exportAll) {
        usersToExport = exportData;
      } else {
        const selectedUsers = exportData.users.filter((u) =>
          selectedIds.includes(u.id)
        );
        usersToExport = {
          ...exportData,
          users: selectedUsers,
          filtered_count: selectedUsers.length,
        };
      }

      // Build clean filters for report
      const cleanFilters = {};
      if (filters.sex) cleanFilters.sex = filters.sex;
      if (filters.age_min > 18) cleanFilters.age_min = filters.age_min;
      if (filters.age_max < 100) cleanFilters.age_max = filters.age_max;
      if (filters.risk_level !== undefined) cleanFilters.risk_level = filters.risk_level;
      if (filters.has_recommendations !== undefined) cleanFilters.has_recommendations = filters.has_recommendations;

      if (exportFormat === "pdf") {
        generateBulkReport(usersToExport, cleanFilters, clinician_name);
      } else {
        generateBulkCSV(usersToExport, includeRecommendations);
      }
    } catch (err) {
      setError(t("errors.exportFailed"));
      console.error("Error exporting data:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ margin: "40px 30px" }}>
        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ mb: 1, color: "primary.main" }}>
            {t("title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("description")}
          </Typography>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <FilterPanel
          filters={filters}
          setFilters={setFilters}
          onReset={handleResetFilters}
        />

        {/* Preview Table */}
        <PreviewTable
          users={exportData.users}
          loading={loading}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
        />

        {/* Export Options */}
        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("exportOptions.title")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              gap: 2,
            }}
          >
            {/* Format Selection */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2">{t("exportOptions.format")}:</Typography>
              <ToggleButtonGroup
                value={exportFormat}
                exclusive
                onChange={handleFormatChange}
                size="small"
              >
                <ToggleButton value="pdf">
                  <PictureAsPdfIcon sx={{ mr: 1 }} />
                  {t("formats.pdf")}
                </ToggleButton>
                <ToggleButton value="csv">
                  <TableChartIcon sx={{ mr: 1 }} />
                  {t("formats.csv")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* CSV Option */}
            {exportFormat === "csv" && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeRecommendations}
                    onChange={(e) => setIncludeRecommendations(e.target.checked)}
                    size="small"
                  />
                }
                label={t("exportOptions.includeRecommendations")}
              />
            )}

            {/* Export Buttons */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={() => handleExport(false)}
                disabled={exporting || selectedIds.length === 0}
              >
                {t("buttons.exportSelected")} ({selectedIds.length})
              </Button>
              <Button
                variant="contained"
                startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={() => handleExport(true)}
                disabled={exporting || exportData.users.length === 0}
              >
                {t("buttons.exportAll")} ({exportData.users.length})
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Layout>
  );
}
